import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';

import { useAppDispatch } from 'src/store';
import {
  AdminSettingReqEntity,
  ApplySettingReqEntity,
  CommonSettingReqEntity,
  NotiSettingReqEntity,
  ProxySettingReqEntity,
  DecryptSettingReqEntity,
  ProvisionalSettingReqEntity,
  ScreenSettingReqEntity,
  TakeoutSettingReqEntity,
  PrintPeriodSettingReqEntity,
  PrintFileSettingReqEntity,
  PrintClientSettingReqEntity,
  selectInitialized,
  selectSettingEntities,
  thunkLoadSettings,
  thunkUpdateAdminSetting,
  thunkUpdateApplySetting,
  thunkUpdateCommonSetting,
  thunkUpdateNotiSetting,
  thunkUpdateProxySetting,
  thunkUpdateDecryptSetting,
  thunkUpdateProvisionalSetting,
  thunkUpdateScreenSetting,
  thunkUpdateTakeoutSetting,
  thunkUpdatePrintPeriodSetting,
  thunkUpdatePrintFileSetting,
  thunkUpdatePrintClientSetting,
} from 'src/store/setting';
import { thunkLoadUsersByIds } from 'src/store/fidp/user';
import { UserSearchDto } from 'src/api/fidp/userApi';

// # hooks
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const useSettingStore = (updateByForce = false) => {
  const initialized = useSelector(selectInitialized);
  const settingEntities = useSelector(selectSettingEntities);

  const dispatch = useAppDispatch();

  const boundLoadSettings = useCallback(() => {
    const loadSettingsResult = dispatch(thunkLoadSettings()).then(unwrapResult);
    const loadUsersResult = loadSettingsResult.then((settingEntities) => {
      if (settingEntities.apply.excludeApproverUser.length > 0) {
        return dispatch(thunkLoadUsersByIds(settingEntities.apply.excludeApproverUser)).then(
          unwrapResult
        );
      } else {
        return Promise.resolve({ count: 0, users: [] } as UserSearchDto);
      }
    });
    return Promise.all([loadSettingsResult, loadUsersResult]).then(
      ([settingEntities, userSearchResult]) => settingEntities
    );
  }, [dispatch]);
  const boundUpdateAdminSetting = useCallback(
    (reqEntity: AdminSettingReqEntity) =>
      dispatch(thunkUpdateAdminSetting(reqEntity)).then(unwrapResult),
    [dispatch]
  );

  const boundUpdateApplySetting = useCallback(
    (reqEntity: ApplySettingReqEntity) => {
      const updateApplySettingResult = dispatch(thunkUpdateApplySetting(reqEntity)).then(
        unwrapResult
      );
      const loadUsersResult = updateApplySettingResult.then((applySettingEntity) => {
        if (applySettingEntity.excludeApproverUser.length > 0) {
          return dispatch(thunkLoadUsersByIds(applySettingEntity.excludeApproverUser)).then(
            unwrapResult
          );
        } else {
          return Promise.resolve({ count: 0, users: [] } as UserSearchDto);
        }
      });
      return Promise.all([updateApplySettingResult, loadUsersResult]).then(
        ([applySettingEntity, userSearchResult]) => applySettingEntity
      );
    },
    [dispatch]
  );

  const boundUpdateCommonSetting = useCallback(
    (reqEntity: CommonSettingReqEntity) =>
      dispatch(thunkUpdateCommonSetting(reqEntity)).then(unwrapResult),
    [dispatch]
  );

  const boundUpdateNotiSetting = useCallback(
    (reqEntity: NotiSettingReqEntity) =>
      dispatch(thunkUpdateNotiSetting(reqEntity)).then(unwrapResult),
    [dispatch]
  );

  const boundUpdateProxySetting = useCallback(
    (reqEntity: ProxySettingReqEntity) =>
      dispatch(thunkUpdateProxySetting(reqEntity)).then(unwrapResult),
    [dispatch]
  );

  const boundUpdateDecryptSetting = useCallback(
    (reqEntity: DecryptSettingReqEntity) =>
      dispatch(thunkUpdateDecryptSetting(reqEntity)).then(unwrapResult),
    [dispatch]
  );

  const boundUpdateProvisionalSetting = useCallback(
    (reqEntity: ProvisionalSettingReqEntity) =>
      dispatch(thunkUpdateProvisionalSetting(reqEntity)).then(unwrapResult),
    [dispatch]
  );

  const boundUpdateScreenSetting = useCallback(
    (reqEntity: ScreenSettingReqEntity) =>
      dispatch(thunkUpdateScreenSetting(reqEntity)).then(unwrapResult),
    [dispatch]
  );

  const boundUpdateTakeoutSetting = useCallback(
    (reqEntity: TakeoutSettingReqEntity) =>
      dispatch(thunkUpdateTakeoutSetting(reqEntity)).then(unwrapResult),
    [dispatch]
  );

  const boundUpdatePrintPeriodSetting = useCallback(
    (reqEntity: PrintPeriodSettingReqEntity) =>
      dispatch(thunkUpdatePrintPeriodSetting(reqEntity)).then(unwrapResult),
    [dispatch]
  );

  const boundUpdatePrintFileSetting = useCallback(
    (reqEntity: PrintFileSettingReqEntity) =>
      dispatch(thunkUpdatePrintFileSetting(reqEntity)).then(unwrapResult),
    [dispatch]
  );

  const boundUpdatePrintClientSetting = useCallback(
    (reqEntity: PrintClientSettingReqEntity) =>
      dispatch(thunkUpdatePrintClientSetting(reqEntity)).then(unwrapResult),
    [dispatch]
  );

  useEffect(() => {
    if (updateByForce || !initialized) {
      boundLoadSettings();
    }
  }, [boundLoadSettings, updateByForce, initialized]);

  return {
    initialized,
    settingEntities,
    loadSettings: boundLoadSettings,
    updateAdminSetting: boundUpdateAdminSetting,
    updateApplySetting: boundUpdateApplySetting,
    updateCommonSetting: boundUpdateCommonSetting,
    updateNotiSetting: boundUpdateNotiSetting,
    updateProxySetting: boundUpdateProxySetting,
    updateDecryptSetting: boundUpdateDecryptSetting,
    updateProvisionalSetting: boundUpdateProvisionalSetting,
    updateScreenSetting: boundUpdateScreenSetting,
    updateTakeoutSetting: boundUpdateTakeoutSetting,
    updatePrintPeriodSetting: boundUpdatePrintPeriodSetting,
    updatePrintFileSetting: boundUpdatePrintFileSetting,
    updatePrintClientSetting: boundUpdatePrintClientSetting,
  };
};

export default useSettingStore;
